import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { Tooltip } from '@meitu/saas-ui';
import IconFont from '@meitu/xdesign-icon-font';
import { useFramework } from '@/framework';
import { useLocaleStore } from '@/i18n';
import { HIERARCHY_EVENTS } from '../../constants';
import { getHierarchyFrameId, onHierarchyFrameIdChange } from '../../frame-source';
import './style.less';

type Props = {
    disabled?: boolean;
    disableWhenEmpty?: boolean;
    open: boolean;
    onOpenChange: (open: boolean) => void;
};

export const Trigger: React.FC<Props> = ({ disabled = false, disableWhenEmpty = false, open, onOpenChange }) => {
    const { $t } = useLocaleStore();
    const framework = useFramework();
    const { events, flatLayer, frame } = framework.service;
    const [rootLayerCount, setRootLayerCount] = useState(
        () => frame.getLayerIds(getHierarchyFrameId(framework)).length,
    );
    const unavailable = disabled || (disableWhenEmpty && rootLayerCount === 0);

    useEffect(() => {
        if (!disableWhenEmpty) return;

        const update = () => {
            const count = frame.getLayerIds(getHierarchyFrameId(framework)).length;
            setRootLayerCount(count);
            if (count === 0) onOpenChange(false);
        };
        const unsubscribeFrame = onHierarchyFrameIdChange(framework, update);
        const layerListener = flatLayer.onChange((_layerId, _layer, args) => {
            if (args.isAdd || args.isDelete) update();
        });
        return () => {
            unsubscribeFrame();
            events.off(layerListener);
        };
    }, [disableWhenEmpty, events, flatLayer, frame, framework, onOpenChange]);

    const handleOpenChange = () => {
        if (unavailable) return;
        const next = !open;

        onOpenChange(next);
        if (next) events.emit(HIERARCHY_EVENTS.OPEN);
    };

    return (
        <Tooltip placement="top" title={unavailable ? '' : $t((v) => v.plugin.hierarchy.layer)} trigger="hover">
            <span
                className={classNames('hierarchy-trigger', {
                    'hierarchy-trigger--active': open,
                    'hierarchy-trigger--disabled': unavailable,
                })}
                onClick={() => handleOpenChange()}
            >
                <IconFont name="LayerBold" fontSize={16} />
            </span>
        </Tooltip>
    );
};
